import { useEffect } from 'react';
import { useContextStore } from '@/stores/context.store';
import { useAuthStore } from '@/stores/auth.store';
import { clinicsService } from '@/services/api/clinics.service';
import { doctorsService } from '@/services/api/doctors.service';

/**
 * Hook to hydrate context data from localStorage on app initialization
 * This loads the full Clinic and Doctor objects from the backend based on persisted IDs
 */
export function useContextHydration() {
  const { clinicId, doctorId, isHydrated, hydrateFromIds } = useContextStore();
  const { isAuthenticated, isInitialized } = useAuthStore();

  useEffect(() => {
    console.log('[Context Hydration] useEffect triggered:', { 
      clinicId, 
      doctorId, 
      isHydrated, 
      isAuthenticated, 
      isInitialized 
    });

    // Skip if auth not initialized yet
    if (!isInitialized) {
      console.log('[Context Hydration] Auth not initialized yet, skipping');
      return;
    }

    // Skip if not authenticated
    if (!isAuthenticated) {
      console.log('[Context Hydration] User not authenticated, skipping');
      // Clear any persisted IDs if not authenticated
      if (clinicId || doctorId) {
        hydrateFromIds(null, null);
      }
      return;
    }

    // Skip if already hydrated
    if (isHydrated) {
      console.log('[Context Hydration] Already hydrated, skipping');
      return;
    }

    const hydrateContext = async () => {
      console.log('[Context Hydration] Starting hydration...', { clinicId, doctorId });
      
      try {
        // If no IDs are stored, mark as hydrated immediately
        if (!clinicId && !doctorId) {
          console.log('[Context Hydration] No IDs stored, marking as hydrated');
          hydrateFromIds(null, null);
          return;
        }

        let clinic = null;
        let doctor = null;

        // Load clinic if we have a clinicId
        if (clinicId) {
          try {
            console.log('[Context Hydration] Loading clinic:', clinicId);
            const clinics = await clinicsService.getAccessible();
            clinic = clinics.find((c) => c.id === clinicId) || null;
            
            if (!clinic) {
              console.warn('[Context Hydration] Stored clinic ID not found in accessible clinics, clearing context');
              hydrateFromIds(null, null);
              return;
            }
            console.log('[Context Hydration] Clinic loaded:', clinic.name);
          } catch (error) {
            console.error('[Context Hydration] Failed to load clinic:', error);
            hydrateFromIds(null, null);
            return;
          }
        }

        // Load doctor if we have a doctorId
        if (doctorId) {
          try {
            console.log('[Context Hydration] Loading doctor:', doctorId);
            const doctors = await doctorsService.getAccessible();
            doctor = doctors.find((d) => d.id === doctorId) || null;
            
            if (!doctor) {
              console.warn('[Context Hydration] Stored doctor ID not found in accessible doctors, clearing doctor');
              hydrateFromIds(clinic, null);
              return;
            }
            console.log('[Context Hydration] Doctor loaded:', doctor.name);
          } catch (error) {
            console.error('[Context Hydration] Failed to load doctor:', error);
            hydrateFromIds(clinic, null);
            return;
          }
        }

        console.log('[Context Hydration] Hydration complete', { clinic: clinic?.name, doctor: doctor?.name });
        hydrateFromIds(clinic, doctor);
      } catch (error) {
        console.error('[Context Hydration] Failed to hydrate context:', error);
        hydrateFromIds(null, null);
      }
    };

    hydrateContext();
  }, [clinicId, doctorId, isHydrated, isAuthenticated, isInitialized, hydrateFromIds]);
}
